/**
 * C10 — the guided-capture just-in-time disclosure, treated as compliance copy rather than as UI text.
 *
 * **Why this file exists.** VCIP-REG ratified consent clause (4)'s sufficiency for officer-fired
 * in-call captures *on the basis of the on-screen panel as it currently reads* — what is captured,
 * what it is compared against, who processes it and where, retention, no-AI-training, the withdrawal
 * route, and the recorded decline path, behind an affirmative "I understand — continue". Their words:
 * a copy tweak, a well-meaning shortening or a translation pass can remove the processor, retention
 * or no-training lines and thereby retroactively invalidate the basis of that ruling, **with nothing
 * in the audit trail recording that it happened**. The consent notice beside it is immutable,
 * versioned and stamped per session; the disclosure carrying half its weight was ordinary i18n copy.
 *
 * So the keys below are LOAD-BEARING. Three things follow:
 *
 * 1. They are enumerated here, not inferred from a prefix — a new `droom.gcap.purpose.*` key must be
 *    added deliberately, and removing one from this list is as visible as removing the line.
 * 2. `disclosureSha256` digests them AS RENDERED, per locale. The digest travels to the server on ack
 *    (`POST …/prompts/{id}:ack`) and is stored on the prompt, so an examiner can reconstruct what a
 *    given customer read, in their own language.
 * 3. `disclosureVersion.test.ts` pins the ENGLISH digest against a constant. Any edit to any of these
 *    lines fails the build until someone bumps {@link GCAP_DISCLOSURE_VERSION} and the pinned digest
 *    in the same commit — which is the deliberate act the ruling requires, rather than a silent one.
 *
 * **Bumping the version is not forbidden — it is the point.** Change the text, bump the version,
 * update the pin, and route the new text to VCIP-REG. What is forbidden is the change that leaves no
 * trace.
 */

/** The disclosure contract version. Bump on ANY change to the load-bearing lines below. */
export const GCAP_DISCLOSURE_VERSION = 'gcap-disclosure-v1';

/**
 * The load-bearing lines, in digest order. Order is part of the contract: reordering changes the
 * digest, because the sequence a customer reads them in is part of what they were shown.
 *
 * Each entry is one of VCIP-REG's four sufficiency reasons made concrete — what/comparedWith
 * (the act), processor (who and where), retention (how long), noTraining (what it is NOT used for),
 * withdraw + decline (that stopping is a real, recorded option), agree (the affirmative step).
 */
export const GCAP_DISCLOSURE_KEYS = [
  'droom.gcap.purpose.title',
  'droom.gcap.purpose.what',
  'droom.gcap.purpose.comparedWith',
  'droom.gcap.purpose.processor',
  'droom.gcap.purpose.retention',
  'droom.gcap.purpose.noTraining',
  'droom.gcap.purpose.withdraw',
  'droom.gcap.purpose.agree',
  'droom.gcap.decline',
] as const;

export type GcapDisclosureKey = (typeof GCAP_DISCLOSURE_KEYS)[number];

/** Canonical serialization of the rendered disclosure — the digest input. */
export function disclosureCanonicalString(rendered: readonly string[]): string {
  return `vcip-gcap-disclosure|${GCAP_DISCLOSURE_VERSION}|${rendered.join('␞')}`;
}

/**
 * sha256 over the disclosure AS RENDERED, hex. Uses WebCrypto, which needs a secure context — on a
 * browser that does not provide it we return null and the ack records an honest unknown rather than
 * a fabricated or weakened digest.
 */
export async function disclosureSha256(rendered: readonly string[]): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  const bytes = new TextEncoder().encode(disclosureCanonicalString(rendered));
  const digest = await subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
